import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ShoppingBag, MapPin } from "lucide-react";
import { formatMXN } from "@/lib/format";
import { useCart } from "@/lib/cartStore";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
    const cart = useCart();
    const reduce = useReducedMotion();
    const discount =
        product.originalPrice && product.originalPrice > product.price
            ? Math.round(
                  ((product.originalPrice - product.price) /
                      product.originalPrice) *
                      100
              )
            : null;

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        cart.add(product, 1);
        toast.success("Agregado al carrito", {
            description: product.name,
        });
    };

    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : Math.min(index * 0.04, 0.4) }}
            data-testid={`product-card-${product.slug}`}
        >
            <Link
                to={`/${product.storeSlug}/${product.slug}`}
                className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 rounded-xl"
            >
                <div className="relative aspect-square rounded-xl overflow-hidden bg-zinc-100 border border-zinc-200">
                    <img
                        src={product.images?.[0]}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />

                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                        {product.isNew && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-white/95 backdrop-blur text-zinc-900 rounded-full border border-zinc-200">
                                Nuevo
                            </span>
                        )}
                        {discount && (
                            <span
                                aria-label={`Descuento del ${discount} por ciento`}
                                className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-indigo-600 text-white rounded-full"
                            >
                                -{discount}%
                            </span>
                        )}
                    </div>

                    <button
                        onClick={handleAdd}
                        data-testid={`product-card-add-${product.slug}`}
                        className="absolute bottom-3 right-3 w-11 h-11 grid place-items-center rounded-full bg-zinc-900 text-white sm:opacity-0 sm:translate-y-2 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 sm:focus:opacity-100 sm:focus:translate-y-0 transition-all duration-300 hover:bg-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        aria-label={`Agregar ${product.name} al carrito`}
                    >
                        <ShoppingBag className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>

                <div className="mt-3 px-0.5">
                    <h3 className="text-sm font-medium text-zinc-900 line-clamp-1 group-hover:text-indigo-600 transition-colors">
                        {product.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-zinc-600 flex items-center gap-1">
                        <span className="truncate">{product.storeName}</span>
                        <span aria-hidden="true">·</span>
                        <MapPin className="w-3 h-3 shrink-0" aria-hidden="true" />
                        <span className="truncate">
                            {product.storeLocation?.city}
                        </span>
                    </p>
                    <div className="mt-1.5 flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-zinc-900">
                            {formatMXN(product.price)}
                        </span>
                        {product.originalPrice && (
                            <span className="text-xs text-zinc-500 line-through">
                                {formatMXN(product.originalPrice)}
                            </span>
                        )}
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
